import React, { useState } from "react";
import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
import { Calendar, Clock, Users } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux.js";
import { MODAL_TYPES } from "../../../store/features/ui/uiTypes.js";
import { closeModal } from "../../../store/features/ui/uiActions.js";
import { createAppointment } from "../../../store/features/appointments/appointmentsActions.js";
import { mockDeals, mockEventTypes } from "../../../data/mockData.js";

const BookAppointmentModal = () => {
  const dispatch = useAppDispatch();
  const { modals } = useAppSelector((state) => state.ui);
  const { items: agendas } = useAppSelector((state) => state.agendas);
  const { user } = useAppSelector((state) => state.auth);
  const { loading } = useAppSelector((state) => state.appointments);

  const modal = modals[MODAL_TYPES.BOOK_APPOINTMENT];
  const isOpen = modal?.open || false;

  const [formData, setFormData] = useState({
    title: "",
    agenda: "",
    eventType: "",
    date: "",
    time: "",
    duration: 60,
    description: "",
    dealId: "",
    attendees: "",
  });

  const [error, setError] = useState("");

  const handleClose = () => {
    dispatch(closeModal(MODAL_TYPES.BOOK_APPOINTMENT));
    setFormData({
      title: "",
      agenda: "",
      eventType: "",
      date: "",
      time: "",
      duration: 60,
      description: "",
      dealId: "",
      attendees: "",
    });
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !formData.title ||
      !formData.agenda ||
      !formData.date ||
      !formData.time
    ) {
      setError("Please fill in all required fields");
      return;
    }

    const startDateTime = new Date(`${formData.date}T${formData.time}`);
    const endDateTime = new Date(
      startDateTime.getTime() + formData.duration * 60000
    );

    const appointmentData = {
      title: formData.title,
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      allDay: false,
      agenda: formData.agenda,
      status: "pending",
      attendees: [
        {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
        },
      ],
      dealId: formData.dealId || null,
      calendlyEventUri: `https://calendly.com/event/${Date.now()}`,
      description: formData.description,
      location: "TBD",
    };

    try {
      await dispatch(createAppointment(appointmentData));
      // dispatch(showSuccessNotification("Appointment booked successfully"));
      handleClose();
    } catch (err) {
      setError("Failed to book appointment. Please try again.");
    }
  };

  const selectedEventType = mockEventTypes.find(
    (et) => et.id === formData.eventType
  );

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <Calendar size={20} className="me-2" />
          Book New Appointment
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter appointment title"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Agenda *</Form.Label>
                <Form.Select
                  name="agenda"
                  value={formData.agenda}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select agenda</option>
                  {agendas.map((agenda) => (
                    <option key={agenda.id} value={agenda.id}>
                      {agenda.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Event Type</Form.Label>
                <Form.Select
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                >
                  <option value="">Select event type</option>
                  {mockEventTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.duration} min)
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Deal Association</Form.Label>
                <Form.Select
                  name="dealId"
                  value={formData.dealId}
                  onChange={handleChange}
                >
                  <option value="">No deal associated</option>
                  {mockDeals.map((deal) => (
                    <option key={deal.id} value={deal.id}>
                      {deal.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Time *</Form.Label>
                <Form.Control
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Duration (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  min="15"
                  max="480"
                  step="15"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter appointment description"
            />
          </Form.Group>

          {selectedEventType && (
            <Alert variant="info" className="mb-3">
              <Clock size={16} className="me-2" />
              <strong>{selectedEventType.name}:</strong>{" "}
              {selectedEventType.description}
            </Alert>
          )}

          {/* <Alert variant="warning" className="mb-0">
            <strong>Note:</strong> This appointment will be created with pending
            status and requires confirmation.
          </Alert> */}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="btn-custom"
          >
            {loading ? (
              <>
                <span className="loading-spinner me-2"></span>
                Booking...
              </>
            ) : (
              "Book Appointment"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BookAppointmentModal;
